import React, { ReactNode, useContext, useEffect, useState } from 'react';

import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  OrganizationDetails,
  OrganizationHierarchy,
} from '@totem/types/organization';
import { UserRestrictions } from '@totem/types/user';
import { isNotNull } from '@totem/utilities/common';
import {
  ORGANIZATION_ENDPOINT,
  ORGANIZATION_HIERARCHY_ENDPOINT,
} from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

type Props = {
  children?: ReactNode;
};

const OrganizationContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [refreshOrgDetails, setRefreshOrgDetails] = useState<number>(0);
  const [orgDetails, setOrgDetails] = useState<OrganizationDetails>(null);
  const [data, setData] = useState<OrganizationHierarchy>(null);
  const [regions, setRegions] = useState<OrganizationHierarchy[]>([]);
  const [buildings, setBuildings] = useState<OrganizationHierarchy[]>([]);
  const [controlSystems, setControlSystems] = useState<OrganizationHierarchy[]>(
    [],
  );
  const [selectedRegion, setSelectedRegion] =
    useState<OrganizationHierarchy>(null);
  const [selectedBuilding, setSelectedBuilding] =
    useState<OrganizationHierarchy>(null);
  const [selectedControlSystem, setSelectedControlSystem] =
    useState<OrganizationHierarchy>(null);

  useEffect(() => {
    setRefreshData(true);
  }, [userProfile.organization.id]);

  useEffect(() => {
    fetch(`${ORGANIZATION_ENDPOINT}/current`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: OrganizationDetails) => {
        setOrgDetails(result);
      });
  }, [userProfile.organization.id, getApiToken, refreshOrgDetails]);

  const findObject = (objectType: string, objectId: string) => {
    switch (objectType) {
      case 'region': {
        const foundItem = regions.find((chk) => chk.id === objectId);
        if (isNotNull(foundItem)) {
          return foundItem;
        }
        return null;
      }
      case 'building': {
        const foundItem = buildings.find((chk) => chk.id === objectId);
        if (isNotNull(foundItem)) {
          return foundItem;
        }
        return null;
      }
      case 'controlSystem': {
        const foundItem = controlSystems.find((chk) => chk.id === objectId);
        if (isNotNull(foundItem)) {
          return foundItem;
        }
        return null;
      }
      default: {
        return null;
      }
    }
  };

  const getBuildings = (orgHierarchy: OrganizationHierarchy) => {
    let buildingArray: OrganizationHierarchy[] = [];
    if (
      typeof orgHierarchy !== 'undefined' &&
      orgHierarchy !== null &&
      typeof orgHierarchy.children !== 'undefined' &&
      orgHierarchy.children !== null
    ) {
      for (let rIdx = 0; rIdx < orgHierarchy.children.length; rIdx++) {
        buildingArray = buildingArray.concat(
          orgHierarchy.children[rIdx].children,
        );
      }
    }
    return buildingArray.sort((compA, compB) =>
      sortStringAscending(compA.name, compB.name),
    );
  };

  const getControlSystems = (orgHierarchy: OrganizationHierarchy) => {
    let controlSystemArray: OrganizationHierarchy[] = [];
    if (
      typeof orgHierarchy !== 'undefined' &&
      orgHierarchy !== null &&
      typeof orgHierarchy.children !== 'undefined' &&
      orgHierarchy.children !== null
    ) {
      for (let rIdx = 0; rIdx < orgHierarchy.children.length; rIdx++) {
        if (
          typeof orgHierarchy.children[rIdx].children !== 'undefined' &&
          orgHierarchy.children[rIdx].children !== null
        ) {
          for (
            let bIdx = 0;
            bIdx < orgHierarchy.children[rIdx].children.length;
            bIdx++
          ) {
            if (
              typeof orgHierarchy.children[rIdx].children[bIdx].children !==
              'undefined'
            ) {
              controlSystemArray = controlSystemArray.concat(
                orgHierarchy.children[rIdx].children[bIdx].children,
              );
            }
          }
        }
      }
    }
    return controlSystemArray.sort((compA, compB) =>
      sortStringAscending(compA.name, compB.name),
    );
  };

  const handleRefresh = () => {
    setRefreshData(true);

    setRefreshOrgDetails((prev) => prev + 1);
  };

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);
      const orgUrl = ORGANIZATION_HIERARCHY_ENDPOINT;

      fetch(`${orgUrl}`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: UserRestrictions) => {
          setData(result.hierarchy);

          setRegions(
            result.hierarchy.children.sort((compA, compB) =>
              sortStringAscending(compA.name, compB.name),
            ),
          );

          setBuildings(getBuildings(result.hierarchy));

          setControlSystems(getControlSystems(result.hierarchy));
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  return (
    <OrganizationContext.Provider
      value={{
        loading: isLoading,
        orgDetails,
        data,
        regions,
        buildings,
        controlSystems,
        selectedRegion,
        selectedBuilding,
        selectedControlSystem,
        setSelectedRegion,
        setSelectedBuilding,
        setSelectedControlSystem,
        findObject,
        doRefresh: handleRefresh,
      }}
    >
      <div>{children}</div>
    </OrganizationContext.Provider>
  );
};

export default OrganizationContainer;
